import type { ReactNode } from 'react';
import * as Label from '@radix-ui/react-label';
import './FormField.css';

export interface FormFieldProps {
  label: ReactNode;
  required?: boolean;
  /** Error message — shown in place of the hint and marks the field. */
  error?: string;
  hint?: ReactNode;
  /** Span the full width of a FormRow grid. */
  full?: boolean;
  children: ReactNode;
  className?: string;
}

// Wraps a control in the existing `.form-field` (label + control + message).
// Radix Label associates the wrapping <label> with the nested control, so the
// label text is clickable and announced. Best for a single input/textarea;
// for a Radix Select pass its own aria-label instead.
export function FormField({
  label,
  required,
  error,
  hint,
  full,
  children,
  className,
}: FormFieldProps): JSX.Element {
  const cls = ['form-field', full ? 'form-field-full' : '', className].filter(Boolean).join(' ');
  return (
    <Label.Root className={cls}>
      <span>
        {label}
        {required && (
          <span className="dsr-required" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </span>
      {children}
      {hint && !error && <span className="dsr-field-hint">{hint}</span>}
      {error && <span className="error-text">{error}</span>}
    </Label.Root>
  );
}

export interface FormRowProps {
  /** Column count (default 2). Maps to the existing `.form-grid`. */
  columns?: number;
  className?: string;
  children: ReactNode;
}

export function FormRow({ columns = 2, className, children }: FormRowProps): JSX.Element {
  const cls = ['form-grid', className].filter(Boolean).join(' ');
  const style = columns === 2 ? undefined : { gridTemplateColumns: `repeat(${columns}, 1fr)` };
  return (
    <div className={cls} style={style}>
      {children}
    </div>
  );
}
